import React from 'react'
import { connect, useSelector } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import {
  EntityIdAndType,
  EntityState,
  EntityType,
  EntityTypeEnum,
  Market,
  Product,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  TradingStatus,
  WorkflowActions
} from 'api'
import { Groupbox, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { RadioButtonStripField, TextButton, TextButtonType } from 'view/components/buttons'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'
import { Locale, Translation } from 'locale'
import * as constants from 'view/styles/constants'
import { MarketSelectboxField } from 'view/shared/entity/MarketSelectbox'
import { MicCodeSelectboxField } from 'view/shared/entity/MicCodeSelectbox'
import { StartTimeSelectboxField } from 'view/shared/entity/StartTimeSelectbox'
import { StartTimeZoneSelectboxField } from 'view/shared/entity/StartTimeZoneSelectbox'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { CurrencySelectboxField } from 'view/shared/entity'
import { getTradingStatus } from 'view/shared/datagrid'

export type ProductDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  workflowId: string
  entityId: string
  onClose?: () => unknown
}

interface StateProps {
  productEntity: ReferenceDataEntityType<Product> | null
  availableEvents: ReferenceDataManagementEvent[]
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: Product; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: ProductDialogProps) => ownProps.workflowId
const entityIdSelector = (_state: {}, ownProps: ProductDialogProps) => ownProps.entityId

const mapStateToProps = createStructuredSelector({
  productEntity: createWorkflowReferenceDataEntitySelector(EntityType.PRODUCT, workflowIdSelector, entityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;
  min-height: 260px;
  overflow-y: auto;
  width: 450px;
`

const ProductDialogComponent: React.FunctionComponent<ProductDialogProps> = props => {
  const { workflowId, productEntity, availableEvents, onUpdate, onSubmitWorkflow, onCancelWorkflow, onClose, entityId, showDialog } = props
  const getProduct = useSelector(createEntityGetterSelector(EntityType.PRODUCT))

  if (!productEntity) {
    return null
  }

  const product = productEntity.data
  const originalProduct = getProduct(entityId)

  const title = Locale.translate(productEntity.entityState === EntityState.NEW ? Translation.TC_NEW_PRODUCT : Translation.TC_EDIT_PRODUCT)

  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const hasValidationIssues = !!productEntity.validationIssues && productEntity.validationIssues.length > 0

  const updateHandler = <K extends keyof Product>(key: K) => (value: Product[K]) => {
    onUpdate({
      entityId: product.id,
      entityType: EntityTypeEnum.PRODUCT,
      updatedEntity: { ...product, [key]: value },
      workflowId
    })
  }

  const onSave = async () => {
    const micHasChanged = originalProduct ? product.micCode !== originalProduct.micCode : false
    const messageText = Locale.translate(Translation.SC_COLLATERAL_REPORTING_WARNING_MESSAGE)

    if (micHasChanged) {
      const result = await showDialog({
        type: NotificationType.Confirm,
        title: Translation.TC_COLLATERAL_SAVE_CONFIRMATION,
        messageText: `${messageText} \n \n Are you sure you want to continue?`,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO
      })

      if (result === Translation.TC_BUTTON_YES) {
        onSubmitWorkflow(workflowId)
        if (onClose) {
          onClose()
        }
      }
    } else {
      onSubmitWorkflow(workflowId)
      if (onClose) {
        onClose()
      }
    }
  }

  const handleClose = () => {
    onCancelWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  return (
    <ModalWindow title={title}>
      <Content>
        <DialogRow>
          <DialogField>
            <RadioButtonStripField
              dataTestId={'ProductDialog-TextInputField-TradingStatus'}
              label={Locale.translate(Translation.TC_PRIMARY_TRADING_STATUS)}
              options={[Translation.TC_ENABLED, Translation.TC_DISABLED]}
              selectedOption={getTradingStatus(product.tradingStatus)}
              buttonWidth={80}
              disabled={true}
              onUpdate={(status: Translation) => {
                updateHandler('tradingStatus')(status === Translation.TC_ENABLED ? TradingStatus.ENABLED : TradingStatus.DISABLED)
              }}
            />
          </DialogField>

          <DialogField>
            <RadioButtonStripField
              dataTestId={'ProductDialog-TextInputField-SecondaryTradingStatus'}
              label={Locale.translate(Translation.TC_SECONDARY_TRADING_STATUS)}
              options={[Translation.TC_ENABLED, Translation.TC_DISABLED]}
              selectedOption={getTradingStatus(product.secondaryTradingStatus)}
              buttonWidth={80}
              disabled={true}
              onUpdate={(status: Translation) => {
                updateHandler('secondaryTradingStatus')(status === Translation.TC_ENABLED ? TradingStatus.ENABLED : TradingStatus.DISABLED)
              }}
            />
          </DialogField>
        </DialogRow>
        <TextInputField
          dataTestId={'ProductDialog-TextInputField-ProductName'}
          fieldName="name"
          label={Locale.translate(Translation.TC_PRODUCT_NAME)}
          value={product.name}
          maxLength={constants.defaultMaxTextFieldLength}
          readOnly={readOnly}
          onUpdate={updateHandler('name')}
          validationIssues={productEntity.validationIssues || undefined}
        />
        <DialogRow>
          <DialogField>
            <MarketSelectboxField
              dataTestId={'ProductDialog-MarketSelectboxField-Market'}
              fieldName="market"
              label={Locale.translate(Translation.TC_PRIMARY_MARKET)}
              value={product.market}
              readOnly={readOnly || productEntity.entityState === EntityState.EXISTING}
              onUpdate={updateHandler('market')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <MarketSelectboxField
              dataTestId={'ProductDialog-MarketSelectboxField-SecondaryMarket'}
              fieldName="secondaryMarket"
              label={Locale.translate(Translation.TC_SECONDARY_MARKET)}
              value={product.secondaryMarket}
              excludeOptionKeys={[Market.US]}
              readOnly={readOnly || productEntity.entityState === EntityState.EXISTING}
              onUpdate={updateHandler('secondaryMarket')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <MicCodeSelectboxField
              dataTestId={'ProductDialog-MicCodeSelectboxField-MicCode'}
              fieldName="micCode"
              label={Locale.translate(Translation.TC_PRIMARY_MIC)}
              value={product.micCode}
              readOnly={readOnly}
              onUpdate={updateHandler('micCode')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <MicCodeSelectboxField
              dataTestId={'ProductDialog-MicCodeSelectboxField-SecondaryMicCode'}
              fieldName="secondaryMicCode"
              label={Locale.translate(Translation.TC_SECONDARY_MIC)}
              value={product.secondaryMicCode}
              readOnly={readOnly}
              onUpdate={updateHandler('secondaryMicCode')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <StartTimeSelectboxField
              dataTestId={'ProductDialog-TradingStartTimeSelectboxField-StartTime'}
              fieldName="startTime"
              label={Locale.translate(Translation.TC_PRIMARY_START_TIME)}
              value={product.startTime}
              readOnly={readOnly}
              onUpdate={updateHandler('startTime')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <StartTimeSelectboxField
              dataTestId={'ProductDialog-TradingStartTimeSelectboxField-SecondaryStartTime'}
              fieldName="secondaryStartTime"
              label={Locale.translate(Translation.TC_SECONDARY_START_TIME)}
              value={product.secondaryStartTime}
              readOnly={readOnly}
              onUpdate={updateHandler('secondaryStartTime')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <StartTimeZoneSelectboxField
              dataTestId={'ProductDialog-StartTimeZoneSelectboxField-StartTimeZone'}
              fieldName="startTimeZone"
              label={Locale.translate(Translation.TC_PRIMARY_TIME_ZONE)}
              value={product.startTimeZone}
              readOnly={readOnly}
              onUpdate={updateHandler('startTimeZone')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
          <DialogField>
            <StartTimeZoneSelectboxField
              dataTestId={'ProductDialog-StartTimeZoneSelectboxField-SecondaryStartTimeZone'}
              fieldName="secondaryStartTimeZone"
              label={Locale.translate(Translation.TC_SECONDARY_TIME_ZONE)}
              value={product.secondaryStartTimeZone}
              readOnly={readOnly}
              onUpdate={updateHandler('secondaryStartTimeZone')}
              validationIssues={productEntity.validationIssues || undefined}
            />
          </DialogField>
        </DialogRow>

        <DialogRow>
          <DialogField>
            <CurrencySelectboxField
              dataTestId={'ProductDialog-StartTimeZoneSelectboxField-Currency'}
              label={Locale.translate(Translation.TC_PRIMARY_BILLING_CURRENCY)}
              fieldName="billingCurrency"
              value={product.billingCurrency}
              onUpdate={updateHandler('billingCurrency')}
              validationIssues={productEntity.validationIssues || undefined}
              canClear={true}
            />
          </DialogField>
          <DialogField>
            <CurrencySelectboxField
              dataTestId={'ProductDialog-StartTimeZoneSelectboxField-SecondaryCurrency'}
              label={Locale.translate(Translation.TC_SECONDARY_BILLING_CURRENCY)}
              fieldName="secondaryBillingCurrency"
              value={product.secondaryBillingCurrency}
              onUpdate={updateHandler('secondaryBillingCurrency')}
              validationIssues={productEntity.validationIssues || undefined}
              canClear={true}
            />
          </DialogField>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'ProductDialog-TextButton-CancelButton'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'ProductDialog-TextButton-SaveButton'}
              type={TextButtonType.Primary}
              disabled={readOnly || hasValidationIssues}
              onClick={onSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ProductDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(ProductDialogComponent)
)
